<template>
  <div>
    <div class="d-flex align-center breadcrumb-back-row">
      <breadcrumb :items="breadcrumbs" />
      <span class="breadcrumb-back-link primary--text d-flex align-center cursor-pointer" @click="goBack">
        <v-icon color="primary" size="20" class="mr-1">mdi-chevron-left</v-icon> {{ $t('carbonCredit.back') }}
      </span>
    </div>
    <v-container fluid>
    <v-card elevation="0">
      <v-row class="pt-4">
        <!-- Left: Form -->
        <v-col cols="12" md="4">
          <v-card class="pa-6 pt-0" elevation="0">
            <h3 class="font-weight-bold mb-6">{{ isEditPlot ? $t('carbonCredit.plotTreeMapping.editPlotLocation') : $t('carbonCredit.plotTreeMapping.addPlotLocation') }}</h3>

            <v-form ref="locationForm" v-model="isFormValid">
              <label class="text-caption text-grey-darken-1 mb-1">
                {{ $t('carbonCredit.plotTreeMapping.longitude') }} 
              </label>
              <v-text-field v-model="longitude" outlined dense class="mb-2" :rules="longitudeRules" type="number"
                step="any" :placeholder="$t('carbonCredit.plotTreeMapping.enterLongitude')" />

              <label class="text-caption text-grey-darken-1 mb-1">
                {{ $t('carbonCredit.plotTreeMapping.latitude') }} 
              </label>
              <v-text-field v-model="latitude" outlined dense class="mb-2" :rules="latitudeRules" type="number"
                step="any" :placeholder="$t('carbonCredit.plotTreeMapping.enterLatitude')" />

              <label class="text-caption text-grey-darken-1 mb-1">
                {{ $t('carbonCredit.plotTreeMapping.radius') }} (m)
              </label>
              <v-text-field
                v-model="radius"
                outlined
                dense
                class="mb-2"
                type="number"
                step="any"
                :suffix="'m'"
                :placeholder="$t('carbonCredit.plotTreeMapping.enterRadius')"
                :rules="radiusRules"
              />

              <label class="text-caption text-grey-darken-1 mb-1">
                {{ $t('carbonCredit.plotTreeMapping.additionalNotes') }}
              </label>
              <v-textarea v-model="additionalNotes" outlined dense class="mb-2" :placeholder="$t('enterHere')" />

              <!-- <v-row no-gutters class="d-flex">
                <v-col lg="8" md="8" sm="8" xs="6">
                  <v-text-field v-model="areaSize
                    " type="number" placeholder="1" outlined dense class="rounded-r-0"></v-text-field>
                </v-col>
                <v-col lg="4" md="4" sm="4" xs="6">
                  <v-select :items="areaUnitOptions" item-text="label" item-value="value" dense outlined
                    class="bg rounded-l-0 white--text bg-primary" v-model="areaUnit
                      " hide-details></v-select>
                </v-col>
              </v-row> -->

              <div class="d-flex justify-space-between">
                <v-btn outlined color="primary" class="mr-2" @click="onCancel"
                  style="min-width: 120px">
                  {{ $t('cancel') }}
                </v-btn>
                <v-btn color="primary" dark @click="onSave" style="min-width: 120px">
                  {{ $t('save') }}
                </v-btn>
              </div>
            </v-form>
          </v-card>
        </v-col>

        <!-- Right: Map -->
        <v-col cols="12" md="8" class="pa-0">
          <div class="map-wrapper">
            <div class="search-container">
              <v-text-field 
                ref="searchInput"
                prepend-inner-icon="mdi-magnify" 
                v-model="search"
                :placeholder="$t('carbonCredit.plotTreeMapping.searchAddress')" 
                outlined 
                dense 
                class="map-search"
                @keyup.enter="searchLocation"
                @input="onSearchInput"
                @focus="showAutocomplete = true"
                @blur="handleSearchBlur"
              />
              <!-- Autocomplete dropdown -->
              <v-card v-if="showAutocomplete && autocompleteResults.length > 0" class="autocomplete-list" elevation="5">
                <v-list dense>
                  <v-list-item
                    v-for="(result, index) in autocompleteResults"
                    :key="index"
                    @click="selectAutocompleteResult(result)"
                  >
                    <v-list-item-icon class="mr-3">
                      <v-icon>mdi-map-marker</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ result.description }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </div>
            <div class="map-inner">
              <GmapMap
                ref="plotLocationMap"
                :center="mapCenter"
                :zoom="mapZoom"
                map-type-id="satellite"
                style="width: 100%; height: 100%;"
                @click="onMapClick"
                :options="mapOptions"
              >
                <GmapPolygon
                  v-for="(poly, idx) in projectBoundaryPolygons"
                  :key="`boundary-${idx}`"
                  :paths="poly"
                  :options="projectBoundaryPolygonOptions"
                />
                <GmapMarker
                  :position="markerPosition"
                  :draggable="true"
                  @dragend="onMarkerDragEnd"
                  :clickable="false"
                />
              </GmapMap>
            </div>
            <v-btn 
              class="mx-2 float-right" 
              fab 
              dark 
              small 
              color="white"
              style="margin-top: -39vh;" 
              @click="gotoCurrentLocation"
            >
              <v-icon dark color="gray">
                mdi-crosshairs-gps
              </v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card>
    </v-container>

    <v-dialog v-model="plotOutsideDialog" max-width="480" persistent>
      <v-card class="pa-4 text-center">
        <v-icon color="error" size="56" class="mb-4">mdi-alert</v-icon>
        <h3 class="font-weight-bold mb-3">
          {{ $t('carbonCredit.plotTreeMapping.plotOutsideProjectArea') }}
        </h3>
        <p class="mb-6 px-2">
          {{ plotOutsideDialogMessage }}
        </p>
        <v-btn color="primary" dark min-width="120" @click="plotOutsideDialog = false">
          {{ $t('carbonCredit.plotTreeMapping.close') }}
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CarbonCreditService from '@/_services/CarbonCreditService';
import loadingMixin from "@/mixins/LoadingMixin";
import {
  PROJECT_BOUNDARY_POLYGON_OPTIONS,
  isPointInsideProjectBoundary,
} from '@/utils/kmlParser';

export default {
  name: 'PlotLocation',
  mixins: [loadingMixin],

  mounted() {
    if (this.$route.params.id) {
      this.loadPlotData(this.$route.params.id);
    } else {
      this.populateFromFarmLocation();
      this.initializeMap();
    }
    this.$nextTick(() => this.fitMapToProjectBoundary());
  },

  data() {
    return {
      isFormValid: false,
      longitude: '',
      latitude: '',
      radius: 2,
      additionalNotes: '',
      search: '',
      country: '',
      mapCenter: { lat: -12.0464, lng: -77.0428 },
      mapZoom: 18,
      markerPosition: { lat: -12.0464, lng: -77.0428 },
      projectBoundaryPolygonOptions: PROJECT_BOUNDARY_POLYGON_OPTIONS,
      plotOutsideDialog: false,
      mapOptions: {
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: 'cooperative'
      },
      longitudeRules: [
        v => !!v || 'Longitude is required',
        v => !isNaN(v) || 'Longitude must be a number',
        v => (v >= -180 && v <= 180) || 'Longitude must be between -180 and 180'
      ],
      latitudeRules: [
        v => !!v || 'Latitude is required',
        v => !isNaN(v) || 'Latitude must be a number',
        v => (v >= -90 && v <= 90) || 'Latitude must be between -90 and 90'
      ],
      radiusRules: [
        v => (v != null && v !== '') || 'Radius is required',
        v => !isNaN(parseFloat(v)) || 'Radius must be a number',
        v => parseFloat(v) > 0 || 'Radius must be greater than 0'
      ],
      showAutocomplete: false,
      autocompleteResults: [],
      searchTimeout: null
    }
  },

  computed: {
    ...mapGetters({
      projectKmlBoundary: 'carbonCredit/getProjectKmlBoundary',
    }),
    projectBoundaryPolygons() {
      return this.projectKmlBoundary?.polygons || [];
    },
    hasProjectBoundary() {
      return this.projectBoundaryPolygons.length > 0;
    },
    plotOutsideDialogMessage() {
      const project = this.projectKmlBoundary?.projectTitle || 'this project';
      return this.$t('carbonCredit.plotTreeMapping.plotOutsideProjectAreaMessage', {
        project,
      });
    },
    isEditPlot() {
      return !!this.$route.params.id;
    },
    breadcrumbs() {
      const requestId = this.$route.query.requestId;
      const addPlotHref = requestId
        ? `/carbon-credit/tree-plot-add/${requestId}`
        : '/carbon-credit/tree-plot-add';
      const addPlotText = requestId
        ? this.$t('carbonCredit.plotTreeMapping.editPlotTreeMappingRequest')
        : this.$t('carbonCredit.plotTreeMapping.addPlotTree');
      return [
        {
          text: this.$t('carbonCredit.dashboard'),
          disabled: false,
          href: '/dashboard',
          exact: true,
        },
        {
          text: this.$t('carbonCredit.title'),
          disabled: false,
          href: '/carbon-credit/projects',
        },
        {
          text: this.$t('carbonCredit.plotTreeMapping.title'),
          disabled: false,
          href: '/carbon-credit/plot-tree-mapping',
        },
        {
          text: addPlotText,
          disabled: false,
          href: addPlotHref,
        },
        {
          text: this.$t('carbonCredit.plotTreeMapping.choosePlotLocation'),
          disabled: true,
          href: '#',
          exact: true,
        },
      ];
    },
  },

  watch: {
    longitude(newVal) {
      if (newVal && this.latitude) {
        this.updateMarkerPosition();
      }
    },
    latitude(newVal) {
      if (newVal && this.longitude) {
        this.updateMarkerPosition();
      }
    }
  },

  methods: {
    plotTreeMappingApiContext() {
      const draft = this.$store.getters['carbonCredit/getAddPlotTreeDraft'];
      return {
        selectedProjectId: draft?.selectedProjectId,
        projectOptions: draft?.projectOptions,
        country: draft?.requestCountry,
      };
    },
    goBack() {
      this.navigateBackToAddPlotTree();
    },

    onCancel() {
      this.navigateBackToAddPlotTree();
    },

    navigateBackToAddPlotTree() {
      const requestId = this.$route.query.requestId;
      this.$router.push({
        name: 'treePlotAdd',
        params: requestId ? { id: requestId } : {}
      });
    },

    populateFromFarmLocation() {
      const saved = this.$store.getters['carbonCredit/getFarmLocation'];
      if (saved && saved.latitude != null && saved.longitude != null) {
        const lat = parseFloat(saved.latitude);
        const lng = parseFloat(saved.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          this.latitude = String(saved.latitude);
          this.longitude = String(saved.longitude);
          this.search = saved.address || '';
          this.country = saved.country || '';
          this.radius = this.radius || 2;
          this.mapCenter = { lat, lng };
          this.markerPosition = { lat, lng };
          if (!this.country) {
            this.$nextTick(() => this.reverseGeocode(lat, lng));
          }
        }
      }
    },

    reverseGeocode(lat, lng) {
      if (typeof window.google === 'undefined' || !window.google.maps) return;
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          this.search = results[0].formatted_address || '';
          const countryComp = (results[0].address_components || []).find(
            c => c.types && c.types.includes('country')
          );
          if (countryComp) {
            this.country = countryComp.long_name || countryComp.short_name || '';
          }
        }
      });
    },

    updateMarkerPosition() {
      if (this.longitude && this.latitude) {
        this.markerPosition = {
          lat: parseFloat(this.latitude),
          lng: parseFloat(this.longitude)
        };
        // Don't update map center - keep it stable
        // this.mapCenter = this.markerPosition;
      }
    },

    onMapClick(event) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      this.latitude = lat.toFixed(6);
      this.longitude = lng.toFixed(6);
      this.markerPosition = { lat, lng };
      this.reverseGeocode(lat, lng);
    },

    onMarkerDragEnd(event) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      this.latitude = lat.toFixed(6);
      this.longitude = lng.toFixed(6);
      this.markerPosition = { lat, lng };
      this.reverseGeocode(lat, lng);
    },

    async searchLocation() {
      if (!this.search.trim()) return;

      this.startLoading();

      try {
        // Wait for Google Maps API to be available
        if (typeof window.google === 'undefined' || !window.google.maps) {
          // If Google Maps API is not loaded, try to load it
          await this.loadGoogleMapsAPI();
        }

        // Create a geocoder instance
        const geocoder = new window.google.maps.Geocoder();
        
        // Search for the address
        geocoder.geocode({ address: this.search }, (results, status) => {
          this.stopLoading();
          if (status === 'OK' && results[0]) {
            const location = results[0].geometry.location;
            const lat = location.lat();
            const lng = location.lng();
            this.latitude = lat.toFixed(6);
            this.longitude = lng.toFixed(6);
            this.markerPosition = { lat, lng };
            this.mapCenter = { lat, lng };
            const countryComp = (results[0].address_components || []).find(
              c => c.types && c.types.includes('country')
            );
            if (countryComp) {
              this.country = countryComp.long_name || countryComp.short_name || '';
            }
          } else {
            this.$notify({
              title: 'Error',
              text: 'Location not found. Please try a different search term.',
              type: 'error'
            });
          }
        });
      } catch (error) {
        this.stopLoading();
        console.error('Error searching location:', error);
        this.$notify({
          title: 'Error',
          text: 'Error searching location. Please try again.',
          type: 'error'
        });
      }
    },

    loadGoogleMapsAPI() {
      return new Promise((resolve, reject) => {
        // Check if Google Maps API is already loaded
        if (typeof window.google !== 'undefined' && window.google.maps) {
          resolve();
          return;
        }

        // If not loaded, show a message to the user
        this.$notify({
          title: 'Info',
          text: 'Google Maps API is loading. Please try searching again in a moment.',
          type: 'info'
        });
        
        // Wait a bit and try again
        setTimeout(() => {
          if (typeof window.google !== 'undefined' && window.google.maps) {
            resolve();
          } else {
            reject(new Error('Google Maps API not available'));
          }
        }, 2000);
      });
    },

    initializeMap() {
      // Only use GPS if we don't already have a location (e.g. from farm)
      const hasLocation = this.latitude && this.longitude && this.markerPosition?.lat != null;
      if (hasLocation) return;

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            this.mapCenter = { lat, lng };
            this.markerPosition = { lat, lng };
            this.latitude = lat.toFixed(6);
            this.longitude = lng.toFixed(6);
            this.reverseGeocode(lat, lng);
          },
          (error) => {
            console.log('Geolocation error:', error);
          }
        );
      }
    },

    fitMapToProjectBoundary() {
      if (!this.projectBoundaryPolygons.length || !this.$refs.plotLocationMap) return;
      this.$refs.plotLocationMap.$mapPromise.then((map) => {
        const bounds = new window.google.maps.LatLngBounds();
        this.projectBoundaryPolygons.forEach((poly) => {
          poly.forEach((p) => bounds.extend(p));
        });
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds);
        }
      });
    },

    validatePlotInsideProjectBoundary() {
      if (!this.hasProjectBoundary) return true;
      const lat = parseFloat(this.latitude);
      const lng = parseFloat(this.longitude);
      return isPointInsideProjectBoundary(lat, lng, this.projectBoundaryPolygons);
    },

    async onSave() {
      if (!this.$refs.locationForm.validate()) {
        this.$notify({
          title: 'Error',
          text: 'Please fill in all required fields',
          type: 'error'
        });
        return;
      }

      if (!this.validatePlotInsideProjectBoundary()) {
        this.plotOutsideDialog = true;
        return;
      }

      const countryVal = (this.country || '').trim();
      if (!countryVal && !this.$route.params.id) {
        this.$notify({
          title: 'Error',
          text: 'Country is required to generate a valid plot number. Please select a location on the map or use search to set an address.',
          type: 'error'
        });
        return;
      }

      this.startLoading();

      try {
        const radiusMeters = parseFloat(this.radius) || 2;
        // Backend stores radius in centimeters for plots; convert meters -> centimeters
        const radiusCm = radiusMeters * 100;

        const payload = {
          longitude: parseFloat(this.longitude),
          latitude: parseFloat(this.latitude),
          radius: radiusCm,
          notes: this.additionalNotes,
          country: countryVal || undefined,
        };
        let response;
        if (this.$route.params.id) {
          // Edit mode: update existing plot
          response = await CarbonCreditService.updateTreePlot(this.$route.params.id, payload);
        } else {
          // Add mode: create new plot
          response = await CarbonCreditService.addPlotTreeLocation(payload);
        }
          if (response.success) {
          this.$notify({
            title: 'Success',
            text: response.message || (this.$route.params.id ? 'Location updated successfully!' : 'Location saved successfully!'),
            type: 'success'
          });

          if (this.$route.params.id) {
            this.$router.go(-1);
          } else {
            const createdPlot = response.data;
            if (createdPlot && createdPlot.id) {
              this.$store.dispatch('carbonCredit/setNewlyAddedPlot', createdPlot);
            }
            const requestId = this.$route.query.requestId;
            this.$router.push({
              name: 'treePlotAdd',
              params: requestId ? { id: requestId } : {}
            });
          }
        } else {
          this.$notify({
            title: 'Error',
            text: response.message || (this.$route.params.id ? 'Failed to update location' : 'Failed to save location'),
            type: 'error'
          });
        }
      } catch (error) {
        console.log(error);
        this.$notify({
          title: 'Error',
          text: 'An error occurred while saving the location',
          type: 'error'
        });
      } finally {
        this.stopLoading();
      }
    },

    gotoCurrentLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            this.mapCenter = { lat, lng };
            this.markerPosition = { lat, lng };
            this.latitude = lat.toFixed(6);
            this.longitude = lng.toFixed(6);
            this.reverseGeocode(lat, lng);
          },
          (error) => {
            console.log('Geolocation error:', error);
            this.$notify({
              title: 'Error',
              text: 'Unable to get current location. Please check your browser permissions.',
              type: 'error'
            });
          }
        );
      } else {
        this.$notify({
          title: 'Error',
          text: 'Geolocation is not supported by this browser.',
          type: 'error'
        });
      }
    },

    onSearchInput(value) {
      if (value.length < 3) {
        this.autocompleteResults = [];
        return;
      }

      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Debounce the search
      this.searchTimeout = setTimeout(() => {
        this.performAutocompleteSearch(value);
      }, 300);
    },

    handleSearchBlur() {
      // Delay hiding autocomplete to allow for clicks
      setTimeout(() => {
        this.showAutocomplete = false;
      }, 200);
    },

    selectAutocompleteResult(result) {
      this.search = result.description;
      this.showAutocomplete = false;
      
      // Get the place details
      this.getPlaceDetails(result.place_id);
    },

    async performAutocompleteSearch(query) {
      try {
        // Wait for Google Maps API to be available
        if (typeof window.google === 'undefined' || !window.google.maps) {
          await this.loadGoogleMapsAPI();
        }

        const service = new window.google.maps.places.AutocompleteService();
        
        service.getPlacePredictions({
          input: query,
          types: ['geocode', 'establishment'],
          componentRestrictions: { country: [] } // Search worldwide
        }, (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            this.autocompleteResults = predictions;
          } else {
            this.autocompleteResults = [];
          }
        });
      } catch (error) {
        console.error('Autocomplete search error:', error);
        this.autocompleteResults = [];
      }
    },

    async getPlaceDetails(placeId) {
      try {
        const service = new window.google.maps.places.PlacesService(document.createElement('div'));
        
        service.getDetails({
          placeId: placeId,
          fields: ['geometry', 'formatted_address', 'address_components']
        }, (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            this.latitude = lat.toFixed(6);
            this.longitude = lng.toFixed(6);
            this.markerPosition = { lat, lng };
            this.mapCenter = { lat, lng };
            const countryComp = (place.address_components || []).find(
              c => c.types && c.types.includes('country')
            );
            if (countryComp) {
              this.country = countryComp.long_name || countryComp.short_name || '';
            }
          }
        });
      } catch (error) {
        console.error('Error getting place details:', error);
        this.$notify({
          title: 'Error',
          text: 'Error getting location details. Please try again.',
          type: 'error'
        });
      }
    },

    async loadPlotData(plotId) {
      this.startLoading();
      try {
        const response = await CarbonCreditService.getTreePlotById(plotId);
        if (response.success) {
          const plot = response.data;
          this.longitude = plot.longitude || '';
          this.latitude = plot.latitude || '';
          // Plot radius is stored in centimeters in the backend; convert to meters for display
          this.radius = plot.radius ? plot.radius / 100 : '';
          this.additionalNotes = plot.notes || '';
          if (plot.latitude && plot.longitude) {
            const lat = parseFloat(plot.latitude);
            const lng = parseFloat(plot.longitude);
            this.mapCenter = { lat, lng };
            this.markerPosition = { lat, lng };
          }
          this.$nextTick(() => this.fitMapToProjectBoundary());
        }
      } catch (error) {
        this.$notify({ title: 'Error', text: 'Failed to load plot location', type: 'error' });
      } finally {
        this.stopLoading();
      }
    }
  }
}
</script>

<style scoped>
.breadcrumb-back-row {
  align-items: center;
  flex-wrap: nowrap;
  gap: 350px;
}
.breadcrumb-back-row ::v-deep .breadcrum {
  margin-bottom: 0;
  padding-top: 18px;
  padding-bottom: 18px;
  padding-right: 0;
}
.breadcrumb-back-link {
  font-size: 14px;
  line-height: 1.5;
  flex-shrink: 0;
}

.map-wrapper {
  position: relative;
  width: 100%;
  height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.map-search {
  position: absolute;
  top: 14%;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  z-index: 2;
}

.search-container {
  position: absolute;
  width: 100%;
  left: 50%;
  transform: translateX(-50%);
  top: 18%;
  z-index: 3;
}

.map-inner {
  width: 96%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
}

::v-deep .v-input__slot {
  background: #fff !important;
  border-radius: 8px !important;
}

::v-deep .v-text-field__details {
  margin-bottom: 8px;
}

::v-deep .v-select__selections {
  padding-top: 8px;
}

::v-deep .v-text-field .v-input__control{
  border-radius: 4px !important;
}

::v-deep .bg-primary .v-input__slot{
  background-color: #03BD72 !important;
  border-color: #03BD72 !important;
}
::v-deep .v-select.v-input--dense .v-select__selection--comma{
  color: #fff !important;
  border-color: #03BD72 !important;
}

::v-deep .bg-primary .v-icon.v-icon{
  color: #fff !important;
}

.autocomplete-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 250px;
  overflow-y: auto;
  z-index: 1000;
  width: 60%;
  margin: auto;
  margin-top: 5%;
}
.autocomplete-list .v-list {
  padding: 0;
}

.autocomplete-item {
  cursor: pointer;
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
  margin-top: 30px;
}

.autocomplete-item:hover {
  background-color: #f5f5f5;
}

.autocomplete-item:last-child {
  border-bottom: none;
}

.autocomplete-item .v-list-item__content {
  padding: 0;
}

.autocomplete-item .v-list-item__title {
  font-size: 14px;
  color: #333;
  line-height: 1.4;
}
</style>
